import React, { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next';
import CheckBoxHLPure from '../checkBoxHL/checkBoxHL.pure';
import moment from 'moment';
import ReactEcharts from "echarts-for-react";
import multiTooltip from './multi-tooltip';

const formatHour = (value) => {
  return moment(value, "HH").format("h A")
}

const formatTime = (value, periodType, startDate) => {
  if (periodType == 'day') return moment(value, "HH").format("hh:mm A")
  else if (periodType == 'month') {
    const month = moment(startDate).get('month')
    return moment(`${value}-${month + 1}`, "DD-MM").format("MMM DD")
  }
}

const processBusinessData = (businessData, monthsInRange) => {
  // Convertir el array de meses a un formato que preserve los duplicados
  const result = monthsInRange.map(month => (
    [month.index,
    businessData.find(item => item[0] === month.index && item[2] === month.year)?.[1] || 0,
    month.year]
  ));

  // Convertir el mapa a un array ordenado por índice de mes
  return result;
};

function TicketVolumeGraph(props) {
  const { data, prevData, tickFormat, tickLongFormat, periodType, startDate, prevTickFormat, prevTickLongFormat, t, i18n } = props;
  const [showPrevPeriod, setShowPrevPeriod] = useState(false)

  useEffect(() => {
    moment.locale(t.language === 'en' ? 'en' : 'es');
  }, [t.language]);

  const periodTitle = useMemo(() => {
    let periodTitle = 'duringYear'
    if (periodType === 'day') {
      periodTitle = 'duringDay'
    } else if (periodType === 'week') {
      periodTitle = 'duringWeek'
    } else if (periodType === 'month') {
      periodTitle = 'duringMonth'
    }
    return periodTitle
  }, [periodType])

  const option = useMemo(() => {
    const graphData = showPrevPeriod ? prevData : data
    if (!graphData) return;

    const ownBusiness = graphData.find(data => data.own_business)
    const otherBusinesses = graphData.find(data => !data.own_business)

    let ownBusinessProcessed = []
    let otherBusinessesProcessed = []

    let formatAxisValueFn
    if (periodType === 'day' || periodType === 'month') {
      formatAxisValueFn = (value) => formatTime(value, periodType, startDate)
      ownBusinessProcessed = ownBusiness?.data || [];
      otherBusinessesProcessed = otherBusinesses?.data || [];
    } else if (periodType === 'week') {
      formatAxisValueFn = (value) => tickLongFormat?.[value] || value;
    } else {
      ownBusinessProcessed = processBusinessData(ownBusiness?.data || [], showPrevPeriod ? prevTickFormat : tickFormat);
      otherBusinessesProcessed = processBusinessData(otherBusinesses?.data || [], showPrevPeriod ? prevTickLongFormat : tickFormat);
    }

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {},
      grid: {
        top: 36, right: 4, bottom: 24, left: 44
      },
      xAxis: {
        type: 'category',
        data: (() => {
          if (periodType === 'month') {
            return ownBusiness?.data.map(item => item[0]);
          } else if (periodType === 'week') {
            return tickFormat || [];
          } if (periodType === 'day') {
            return undefined;
          } else {
            return tickFormat?.map(item => item.name) || [];
          }
        })(),
        containLabel: true,
        axisLabel: {
          formatter: periodType === 'day' ? formatHour : undefined
        }
      },
      yAxis: {
        type: 'value',
        boundaryGap: [0, 0.01]
      },
      tooltip: {
        trigger: 'axis',
        confine: true,
        formatter: (params) => {
          if (!Array.isArray(params) || params.length === 0 || !params[0]) {
            return t('noDataAvailable');
          }

          let firstParam = params[0] || undefined;
          let extra = firstParam.data.extraData || undefined;
          let monthData = {};
          let monthName = '';
          let title = '';
          let seriesList = '';

          switch (periodType) {
            case "year":
              // Buscar el mes correspondiente en longMonthsFormat
              monthData = showPrevPeriod ? prevTickLongFormat.find(item =>
                item.index === extra.index &&
                item.year === extra.year
              ) : tickLongFormat.find(item =>
                item.index === extra.index &&
                item.year === extra.year
              )

              // Título con nombre del mes y año (si se encuentra)
              monthName = monthData?.name || 'Mes desconocido';
              title = `${monthName} ${extra.year}`;

              // Listar todas las series
              seriesList = params.map(param => (
                `${param.marker} ${param.seriesName}: ${param.value.toFixed(2)}`
              )).join('<br/>');

              // Combinar título y lista
              return `${title}<br/>${seriesList}`;

            case "month":
              // Buscar el mes correspondiente en longMonthsFormat
              monthData = showPrevPeriod ? prevTickLongFormat.find(item =>
                item.index === extra.month
              ) : tickLongFormat.find(item =>
                item.index === extra.month
              )

              // Título con nombre del mes y año (si se encuentra)
              monthName = monthData?.name || 'Mes desconocido';
              const monthYear = monthData?.year || '';
              title = `${monthName} ${extra.index} ${monthYear}`;

              // Listar todas las series
              seriesList = params.map(param => (
                `${param.marker} ${param.seriesName}: ${param.value.toFixed(2)}`
              )).join('<br/>');

              // Combinar título y lista
              return `${title}<br/>${seriesList}`;

            case "week":
              return multiTooltip({
                params,
                formatAxisValueFn,
                formatByIndex: true,
                formatFn: (value) => value.toFixed(2)
              })

            case "day":
              return multiTooltip({
                params,
                formatAxisValueFn,
                formatByIndex: true,
                formatFn: (value) => value.toFixed(2)
              })

            default:
              return t('unsupportedPeriodType');
          }
        }
      },
      series: [
        {
          name: t("myBusiness"),
          type: 'bar',
          data: periodType === 'year' || periodType === 'month' ? ownBusinessProcessed?.map(item => ({
            //data: ownBusinessProcessed.map(item => ({
            value: item[1],  // El valor numérico
            extraData: {
              index: item[0],
              month: periodType === 'month' ? item[2] : undefined,
              year: periodType === 'year' ? item[2] : undefined
            }
          })) : ownBusiness?.data || [],
          //data: ownBusiness?.data || [],
          itemStyle: { color: "#00a4e0" }
        },
        {
          name: t("otherBusinesses"),
          type: 'bar',
          data: periodType === 'year' || periodType === 'month' ? otherBusinessesProcessed?.map(item => ({
            //data: ownBusinessProcessed.map(item => ({
            value: item[1],  // El valor numérico
            extraData: {
              index: item[0],
              month: periodType === 'month' ? item[2] : undefined,
              year: periodType === 'year' ? item[2] : undefined
            }
          })) : otherBusinesses?.data || [],
          //data: otherBusinesses?.data || [],
          itemStyle: { color: "#69B75C" }
        }
      ],
      dataZoom: [
        {
          type: 'inside',
          orient: 'horizontal',
          start: 0,
          end: 100,
          minSpan: 10,
        },
        {
          type: 'slider',
          show: false,
        }
      ],
    };

    return option;
  }, [data, prevData, periodType, showPrevPeriod, t, tickFormat, tickLongFormat, prevTickFormat, prevTickLongFormat])

  return (
    <div className="dashboardView-card rasd-month" >
      <div className='rasd-month-heading'>
        <p className="dashboardView-card-title">{t("ticketVolume")} {t(periodTitle)}</p>
        <CheckBoxHLPure
          labelCheckBox={t("previousPeriod")}
          valueCheckBox={showPrevPeriod}
          checkedState={showPrevPeriod}
          onChangeHandler={(e) => setShowPrevPeriod(e.target.checked)}
        />
      </div>

      {
        option
          ? <ReactEcharts option={option} />
          : <p>N/D</p>
      }
    </div>
  )
}

export default TicketVolumeGraph